import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { catchError, Observable, Subject, tap, throwError } from 'rxjs';
import { DailogService } from '../controls/dailog/dailog.service';
import { CommonServiceService } from './common-service.service';
import { environment } from '../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthServiceService {
  private tokenKey = 'authToken';
  loggedIn = false;
  sessionTimeout!: any;
  pageList: any;
  isChatMinimized: boolean = false;
  private sessionDuration!: number;
  private activityListenersAdded = false;
  private storageListenerAdded = false;
  private lastDebounceTime = 0;

  constructor(
    private dialog: DailogService,
    private router: Router,
    private injector: Injector
  ) {
    this.setupCrossTabListener();
    this.restoreSession();
  }

  private restoreSession() {
    const token = localStorage.getItem(this.tokenKey);
    if (!token) return;

    const storedDuration = localStorage.getItem('sessionDuration');
    if (!storedDuration) return;

    const lastActivityStr = localStorage.getItem('lastActivity');
    const duration = Number(storedDuration);
    const lastActivity = lastActivityStr ? Number(lastActivityStr) : 0;
    const elapsed = Date.now() - lastActivity;

    if (!lastActivityStr || elapsed >= duration) {
      // Session expired while app was closed — clear data; AppComponent.ngOnInit will redirect to login
      this.clearAllSessionData();
      return;
    }

    // Session still valid — resume with remaining time
    this.sessionDuration = duration;
    this.loggedIn = true;
    this.attachActivityListeners();

    const remainingTime = duration - elapsed;
    this.sessionTimeout = setTimeout(() => {
      this.checkSessionExpiration();
    }, remainingTime);
  }

  private setupCrossTabListener() {
    if (this.storageListenerAdded) return;

    window.addEventListener('storage', (event) => {
      // When any tab logs out, all tabs should log out
      if (event.key === 'sessionLogout' && event.newValue === 'true') {
        localStorage.removeItem('sessionLogout');
        this.handleLogout('Session ended from another tab');
      }
      // When session expires in one tab, notify other tabs
      if (event.key === 'sessionExpired' && event.newValue === 'true') {
        localStorage.removeItem('sessionExpired');
        this.handleLogout('Session expired due to inactivity');
      }
      // When a fresh login happens in another tab, force this tab to re-authenticate
      if (event.key === 'sessionLogin' && event.newValue) {
        localStorage.removeItem('sessionLogin');
        this.handleLogout('A new session was started in another tab. Please log in again.');
      }
    });

    this.storageListenerAdded = true;
  }

  public spinnerSubject = new Subject<boolean>();
  spinner$ = this.spinnerSubject.asObservable();

  show() {
    this.spinnerSubject.next(true);
  }

  hide() {
    this.spinnerSubject.next(false);
  }

  setToken(token: string) {
    localStorage.setItem(this.tokenKey, token);
  }

  getToken() {
    return localStorage.getItem(this.tokenKey);
  }

  removeToken() {
    localStorage.removeItem(this.tokenKey);
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  setSessionTimeout(minutes: number) {
    this.sessionDuration = minutes * 60 * 1000;
    localStorage.setItem('sessionDuration', this.sessionDuration.toString());

    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout);
    }

    if (this.loggedIn) {
      localStorage.setItem('lastActivity', Date.now().toString());
      this.attachActivityListeners();
      this.scheduleSessionCheck();
    }
  }

  private attachActivityListeners() {
    if (this.activityListenersAdded) return;

    ['click', 'mousemove', 'keypress'].forEach((evt) => {
      window.addEventListener(evt, () => {
        if (!this.loggedIn) return;
        const now = Date.now();
        localStorage.setItem('lastActivity', now.toString());
        // Debounce: reschedule the timer at most once every 5 seconds
        if (now - this.lastDebounceTime > 5000) {
          this.lastDebounceTime = now;
          this.scheduleSessionCheck();
        }
      }, true);
    });

    this.activityListenersAdded = true;
  }

  private scheduleSessionCheck() {
    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout);
    }

    if (!this.loggedIn || !this.sessionDuration) {
      return;
    }

    this.sessionTimeout = setTimeout(() => {
      this.checkSessionExpiration();
    }, this.sessionDuration);
  }

  private checkSessionExpiration() {
    if (!this.loggedIn) return;

    const lastActivityStr = localStorage.getItem('lastActivity');
    const lastActivity = lastActivityStr ? Number(lastActivityStr) : 0;
    const now = Date.now();

    // Check if session has expired
    if (lastActivity === 0 || now - lastActivity >= this.sessionDuration) {
      // Notify other tabs
      localStorage.setItem('sessionExpired', 'true');
      // Logout current tab
      this.handleLogout('Session expired due to inactivity');
    } else {
      // Reschedule for remaining time
      const remainingTime = this.sessionDuration - (now - lastActivity);
      this.sessionTimeout = setTimeout(() => {
        this.checkSessionExpiration();
      }, remainingTime);
    }
  }

  private clearAllSessionData(): void {
    ['authToken', 'username', 'UserId', 'lastActivity', 'sessionDuration', 'LastLogin', 'pageList'].forEach(key => {
      localStorage.removeItem(key);
    });
  }

  private handleLogout(message: string) {
    if (!this.loggedIn) return;

    const common = this.injector.get(CommonServiceService);
    common.rows = [];

    this.clearAllSessionData();

    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout);
    }

    this.loggedIn = false;
    this.activityListenersAdded = false;
    this.isChatMinimized = false;
    this.router.navigateByUrl('/login');

    if (message?.trim()) {
      this.dialog.alertType = 'alert-danger';
      this.dialog.alertMessage = message + '. Please log in again.';
      this.dialog.showAlert(this.dialog.alertType, this.dialog.alertMessage);
    }
  }

  logout() {
    // Remove first so the storage event always fires even if the value was already 'true'
    localStorage.removeItem('sessionLogout');
    localStorage.setItem('sessionLogout', 'true');
    this.handleLogout('');
  }

  setSession(username: string, userId: string): void {
    localStorage.setItem('username', username);
    localStorage.setItem('UserId', userId);
  }

  getSession(): { username: string | null; userId: string | null } {
    return {
      username: localStorage.getItem('username'),
      userId: localStorage.getItem('UserId'),
    };
  }

  clearSession(): void {
    localStorage.removeItem('username');
    localStorage.removeItem('UserId');
  }
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(private router: Router, private authService: AuthServiceService) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const authToken = localStorage.getItem('authToken')
    const userId = localStorage.getItem('UserId');
    const userName = localStorage.getItem('username')
    const excludedUrls = ['/Login/token'];

    // Check if the current request's URL is in the excluded list
    const shouldExclude = excludedUrls.some((url) => req.url.includes(url));

    if (!shouldExclude && authToken && userId && userName && (req.url.startsWith("https://apitabreed.prosolonline.com") || req.url.startsWith("http://172.31.44.5/coda") || req.url.startsWith("https://apiprosol11.prosolonline.com") || req.url.startsWith("https://dev11.prosolonline.com") || req.url.startsWith("http://localhost:15505"))) {

      const authReq = req.clone({
        headers: new HttpHeaders({
          'userId': userId,
          'username': userName,
          'Authorization': `Bearer ` + authToken
        })
      })

      return next.handle(authReq).pipe(
        tap(() => {
          // Refresh activity on successful request (updates lastActivity)
          localStorage.setItem('lastActivity', Date.now().toString());
        }),
        catchError((err) => {
          this.handleError(err);
          return throwError(() => err);
        })
      );
    }

    return next.handle(req).pipe(
      catchError((err) => {
        this.handleError(err);
        return throwError(() => err);
      })
    );
  }

  private handleError(err: any) {
    if (err.status === 0) {
      console.log("Network error: Please check your internet connection or server status.");
    }
    else if (err.status === 401 && !this.router.url.startsWith('/logout')) {
      console.log("Unauthorized token");
      this.authService.logout();
    }
    else if (err.status === 404) {
      console.log("Error", "url not found");
    }
    else {
      console.log('An unexpected error occurred. Please try again later.');
    }
  }
}

export class Utils {
  static months({ count }: { count: number }) {
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul',
      'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    // Return the number of months specified by count
    return months.slice(0, count);
  }
}

export const CanActivateguard: CanActivateFn =
  (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    if (inject(AuthServiceService).isLoggedIn()) {
      inject(AuthServiceService).loggedIn = true;
      return true
    }
    else {
      inject(Router).navigateByUrl('login');
      inject(AuthServiceService).loggedIn = false;
      return false;
    }
  };

